import React, { useEffect, useState } from 'react';
// no navigation - use modal dialogs instead
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Box,
  Avatar,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Visibility as VisibilityIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import { meetingsData } from '../../Data/Kontak/Meeting';


const MeetingPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view' | 'add' | 'edit'
  const [activeItem, setActiveItem] = useState(null);
  const [form, setForm] = useState({
    requesterName: '',
    room: '',
    date: '',
    startTime: '',
    endTime: '',
    attendees: 1,
    purpose: '',
    status: 'Pending',
    notes: ''
  });

  const openModal = (mode, item = null) => {
    setModalMode(mode);
    if (item) {
      setActiveItem(item);
      setForm({
        requesterName: item.requesterName || '',
        room: item.room || '',
        date: item.date || '',
        startTime: item.startTime || '',
        endTime: item.endTime || '',
        attendees: item.attendees || 1,
        purpose: item.purpose || '',
        status: item.status || 'Pending',
        notes: item.notes || ''
      });
    } else {
      setActiveItem(null);
      setForm({ requesterName: '', room: '', date: '', startTime: '', endTime: '', attendees: 1, purpose: '', status: 'Pending', notes: '' });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActiveItem(null);
  };

  const handleFormChange = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const persistData = (next) => {
    setData(next);
    localStorage.setItem('meetings', JSON.stringify(next));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (modalMode === 'add') {
      const id = `m${Date.now()}`;
      const item = { ...form, id, _id: id };
      const next = [item, ...data];
      persistData(next);
      SwalConfig.success('Tersimpan', 'Pengajuan meeting berhasil disimpan', 1400);
      closeModal();
    } else if (modalMode === 'edit' && activeItem) {
      const next = data.map(x => (x.id === activeItem.id || x._id === activeItem._id) ? { ...x, ...form } : x);
      persistData(next);
      SwalConfig.success('Tersimpan', 'Perubahan disimpan', 1200);
      closeModal();
    }
  };

  useEffect(() => {
    // load from localStorage if available
    const stored = localStorage.getItem('meetings');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setData(parsed);
      } catch (e) {
        setData(meetingsData);
      }
    } else {
      setData(meetingsData);
    }
    setLoading(false);
  }, []);

  const handleDelete = (id) => {
    Swal.fire({
      ...SwalConfig.confirm,
      title: 'Hapus Meeting?',
      text: 'Apakah Anda yakin ingin menghapus pengajuan meeting ini?',
      confirmButtonText: 'Ya, hapus'
    }).then((res) => {
      if (res.isConfirmed) {
        const next = data.filter(d => d.id !== id && d._id !== id);
        setData(next);
        localStorage.setItem('meetings', JSON.stringify(next));
        SwalConfig.success('Terhapus', 'Pengajuan meeting dihapus', 1500);
      }
    });
  };

  const filtered = data.filter(d => {
    const q = searchText.toLowerCase();
    return (
      (d.requesterName || '').toLowerCase().includes(q) ||
      (d.room || '').toLowerCase().includes(q) ||
      (d.purpose || '').toLowerCase().includes(q)
    );
  }).sort((a,b) => (a.date||'').localeCompare(b.date||''));

  return (
    <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.06)', borderRadius: 3 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>M</Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>Manajemen Meeting</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Kelola pengajuan ruangan meeting</Typography>
            </Box>
          </Box>
        }
        action={(
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openModal('add')}>
              Tambah Meeting
            </Button>
          </Box>
        )}
      />
      <Divider />
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cari oleh requester, room atau purpose"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{filtered.length}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Total Pengajuan</Typography>
            </Grid>
          </Grid>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>No</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem', minWidth: 200 }}>Requester</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Room</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Tanggal</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Waktu</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem', textAlign: 'center' }}>Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.slice(page*rowsPerPage, page*rowsPerPage+rowsPerPage).map((m, idx) => (
                    <TableRow key={m.id} hover>
                      <TableCell>{idx+1+page*rowsPerPage}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 36, height: 36 }}>{(m.requesterName||'').charAt(0) || 'U'}</Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>{m.requesterName}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{m.purpose}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{m.room}</TableCell>
                      <TableCell>{m.date}</TableCell>
                      <TableCell>{m.startTime} - {m.endTime}</TableCell>
                      <TableCell>
                        <Chip label={m.status} color={m.status === 'Approved' ? 'success' : m.status === 'Rejected' ? 'error' : 'warning'} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton size="small" onClick={() => openModal('view', m)}><VisibilityIcon fontSize="small"/></IconButton>
                          <IconButton size="small" onClick={() => openModal('edit', m)}><EditIcon fontSize="small"/></IconButton>
                          <IconButton size="small" onClick={() => handleDelete(m.id)}><DeleteIcon fontSize="small"/></IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination rowsPerPageOptions={[5,10,25]} component="div" count={filtered.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value,10)); setPage(0); }} />
          </Box>
        )}
      </CardContent>
      
      {/* Modal Dialog for View / Add / Edit */}
      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="md">
        <DialogTitle>
          {modalMode === 'view' && `Detail Meeting - ${activeItem?.requesterName || ''}`}
          {modalMode === 'add' && 'Tambah Pengajuan Meeting'}
          {modalMode === 'edit' && `Edit Pengajuan - ${activeItem?.requesterName || ''}`}
        </DialogTitle>
        <DialogContent dividers>
          {modalMode === 'view' ? (
            <Box sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Requester</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{activeItem?.requesterName}</Typography>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 2 }}>Tanggal & Waktu</Typography>
                  <Typography>{activeItem?.date} • {activeItem?.startTime} - {activeItem?.endTime}</Typography>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 2 }}>Room</Typography>
                  <Typography>{activeItem?.room}</Typography>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 2 }}>Attendees</Typography>
                  <Typography>{activeItem?.attendees}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Purpose</Typography>
                  <Typography sx={{ mb: 2 }}>{activeItem?.purpose || '-'}</Typography>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Notes</Typography>
                  <Typography sx={{ background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f7fafc', p: 2, borderRadius: 1 }}>{activeItem?.notes || '-'}</Typography>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSave} sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><TextField fullWidth label="Requester Name" value={form.requesterName} onChange={handleFormChange('requesterName')} required /></Grid>
                <Grid item xs={12} md={6}><TextField fullWidth label="Room" value={form.room} onChange={handleFormChange('room')} required /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.date} onChange={handleFormChange('date')} required /></Grid>
                <Grid item xs={6} md={4}><TextField fullWidth type="time" label="Start" InputLabelProps={{ shrink: true }} value={form.startTime} onChange={handleFormChange('startTime')} required /></Grid>
                <Grid item xs={6} md={4}><TextField fullWidth type="time" label="End" InputLabelProps={{ shrink: true }} value={form.endTime} onChange={handleFormChange('endTime')} required /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Purpose" value={form.purpose} onChange={handleFormChange('purpose')} required /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Attendees" value={form.attendees} onChange={handleFormChange('attendees')} /></Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={form.status} label="Status" onChange={handleFormChange('status')}>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Notes" value={form.notes} onChange={handleFormChange('notes')} /></Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {modalMode === 'view' ? (
            <Stack direction="row" spacing={1} sx={{ p: 1 }}>
              <Button variant="outlined" onClick={() => { openModal('edit', activeItem); }}>Edit</Button>
              <Button variant="contained" color="primary" onClick={closeModal}>Tutup</Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} sx={{ p: 1 }}>
              <Button variant="outlined" onClick={closeModal}>Batal</Button>
              <Button type="submit" form="" variant="contained" onClick={handleSave}>{modalMode === 'add' ? 'Simpan' : 'Simpan Perubahan'}</Button>
            </Stack>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default MeetingPage;
